import React, { useEffect, useMemo, useState } from "react";
import { useAppNavigate } from "../../hooks/useAppNavigate";
import { useReportJobStore } from "../../store/useReportJobStore";
import type { ReportExportType, ReportGrouping } from "../../types/reportJob";
import { useCountryStore } from "../../store/useCountryStore";
import { useVehicleCategoryStore } from "../../store/useVehicleCategoryStore";
import { useBikeStore } from "../../store/useBikeStore";
import { ArrowLeft } from "lucide-react";
import { BikeSelector } from "../history/BikeSelector";
import { MultiSelectFilter } from "./MultiSelectFilter";

type QuickRange =
  | "last7"
  | "last30"
  | "thisWeek"
  | "lastWeek"
  | "thisMonth"
  | "lastMonth";

function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function getRange(range: QuickRange): { start: string; end: string } {
  const now = new Date();
  const end = new Date(now);
  const start = new Date(now);
  switch (range) {
    case "last7":
      start.setDate(now.getDate() - 6);
      break;
    case "last30":
      start.setDate(now.getDate() - 29);
      break;
    case "thisWeek": {
      const day = now.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      start.setDate(now.getDate() + diff);
      break;
    }
    case "lastWeek": {
      const day = now.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      start.setDate(now.getDate() + diff - 7);
      end.setDate(now.getDate() + diff - 1);
      break;
    }
    case "thisMonth":
      start.setDate(1);
      break;
    case "lastMonth":
      start.setMonth(now.getMonth() - 1, 1);
      end.setMonth(now.getMonth(), 0);
      break;
  }
  return { start: toIsoDate(start), end: toIsoDate(end) };
}

export const ReportCreateView: React.FC = () => {
  const navigateToView = useAppNavigate();
  const { createJob, creating } = useReportJobStore();
  const { countries, loadIfNeeded: loadCountriesIfNeeded } = useCountryStore();
  const { categories, loadIfNeeded: loadCategoriesIfNeeded } =
    useVehicleCategoryStore();
  const { bikes, loadIfNeeded: loadBikesIfNeeded } = useBikeStore();
  const [startDate, setStartDate] = useState(getRange("last7").start);
  const [endDate, setEndDate] = useState(getRange("last7").end);
  const [selectedCountryCodes, setSelectedCountryCodes] = useState<string[]>(
    [],
  );
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedBikeIds, setSelectedBikeIds] = useState<string[]>([]);
  const [exportType, setExportType] = useState<ReportExportType>("summary");
  const [grouping, setGrouping] = useState<ReportGrouping>("daily");
  const [error, setError] = useState("");

  useEffect(() => {
    loadCountriesIfNeeded().catch(console.error);
    loadCategoriesIfNeeded().catch(console.error);
    loadBikesIfNeeded().catch(console.error);
  }, [loadBikesIfNeeded, loadCategoriesIfNeeded, loadCountriesIfNeeded]);

  const quickRanges: { id: QuickRange; label: string }[] = useMemo(
    () => [
      { id: "last7", label: "Last 7 days" },
      { id: "last30", label: "Last 30 days" },
      { id: "thisWeek", label: "This week" },
      { id: "lastWeek", label: "Last week" },
      { id: "thisMonth", label: "This month" },
      { id: "lastMonth", label: "Last month" },
    ],
    [],
  );
  const activeCountries = useMemo(
    () => countries.filter((country) => country.status === "active"),
    [countries],
  );

  const handleQuickRange = (range: QuickRange) => {
    const next = getRange(range);
    setStartDate(next.start);
    setEndDate(next.end);
  };

  const handleSubmit = async () => {
    setError("");
    if (!startDate || !endDate) {
      setError("Start date and end date are required");
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setError("Start date cannot be after end date");
      return;
    }

    try {
      await createJob({
        export_type: exportType,
        grouping: exportType === "raw" ? undefined : grouping,
        filters: {
          start_date: `${startDate}T00:00:00.000Z`,
          end_date: `${endDate}T23:59:59.999Z`,
          country_codes: selectedCountryCodes.length
            ? selectedCountryCodes
            : undefined,
          category_ids: selectedCategoryIds.length
            ? selectedCategoryIds
            : undefined,
          bike_ids: selectedBikeIds.length ? selectedBikeIds : undefined,
        },
      });
      navigateToView("reports");
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Failed to create report job",
      );
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigateToView("reports")}
          className="p-2 rounded-md border border-gray-300 hover:bg-gray-50"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Create Report
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Configure filters and generate summary, summary per vehicles, or raw
            data export.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">
            Quick Date Ranges
          </p>
          <div className="flex flex-wrap gap-2">
            {quickRanges.map((range) => (
              <button
                key={range.id}
                onClick={() => handleQuickRange(range.id)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-700 mb-1">
              Start date
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">End date</label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">
              Country (GPS based)
            </label>
            <MultiSelectFilter
              options={activeCountries.map((c) => ({
                value: c.country_code,
                label: `${c.country_name} (${c.country_code})`,
              }))}
              selected={selectedCountryCodes}
              onChange={setSelectedCountryCodes}
              emptyLabel="All countries"
              searchPlaceholder="Search countries..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave empty to include all countries.
            </p>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">
              Vehicle category
            </label>
            <MultiSelectFilter
              options={categories.map((c) => ({
                value: c.category_id,
                label: c.category_name,
              }))}
              selected={selectedCategoryIds}
              onChange={setSelectedCategoryIds}
              emptyLabel="All categories"
              searchPlaceholder="Search categories..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave empty to include all categories.
            </p>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">
              Vehicles (searchable)
            </label>
            <BikeSelector
              bikes={bikes}
              selectedBikes={selectedBikeIds}
              onChange={setSelectedBikeIds}
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave empty to include all vehicles.
            </p>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">
              Export type
            </label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={exportType}
              onChange={(e) =>
                setExportType(e.target.value as ReportExportType)
              }
            >
              <option value="summary">Summary</option>
              <option value="summary_per_vehicle">Summary per vehicles</option>
              <option value="raw">Raw data</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Grouping</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              value={grouping}
              disabled={exportType === "raw"}
              onChange={(e) => setGrouping(e.target.value as ReportGrouping)}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
            {exportType === "raw" && (
              <p className="text-xs text-gray-500 mt-1">
                Grouping is only used for summary-based exports.
              </p>
            )}
          </div>
        </div>

        {error && <div className="text-sm text-red-600">{error}</div>}

        <div className="flex justify-end">
          <button
            onClick={handleSubmit}
            disabled={creating}
            className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {creating ? "Creating..." : "Generate Report"}
          </button>
        </div>
      </div>
    </div>
  );
};
